@if (isLoading) {
  <!-- Heart + EKG Loader -->
  <div class="loader-overlay" role="status" aria-live="polite">
    <div class="heart-wrapper" aria-hidden="true">
      <svg class="heart-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path
          class="heart-shape"
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4 8.24 4 9.91 5 10.54 6.36
               11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        />
        <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
      </svg>
    </div>
    <p class="loader-text mt-3 fw-semibold text-dark">Loading...</p>
  </div>
}

@if (!isLoading) {
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-md-10 col-lg-8">
        <div class="card shadow rounded-4 border-0">
          <div class="card-body p-4">
            <div class="mb-4 text-center">
              <h2 class="fw-bold mb-1">Doctor Onboarding</h2>
              <p class="text-muted">Fill in your professional details to complete your profile.</p>
            </div>

            <form [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>
              <!-- ── Personal Details ──────────────────────────────── -->
              <h4 class="mb-3 text-primary fw-semibold">Personal Details</h4>
              <hr class="mt-0 mb-4" />

              <div class="row g-3">
                <div class="col-md-4">
                  <label for="title" class="form-label">Title</label>
                  <select id="title" class="form-select text-black" formControlName="title">
                    <option *ngFor="let title of titles" [value]="title">{{ title }}</option>
                  </select>
                  @if (form.controls['title'].touched && form.controls['title'].invalid) {
                    <div class="text-danger small mt-1">Title is required.</div>
                  }
                </div>

                <div class="col-md-4">
                  <label for="gender" class="form-label">Gender</label>
                  <select id="gender" class="form-select text-black" formControlName="gender">
                    <option value="" disabled selected>Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                  @if (form.controls['gender'].touched && form.controls['gender'].invalid) {
                    <div class="text-danger small mt-1">Gender is required.</div>
                  }
                </div>

                <div class="col-md-4">
                  <label for="experience" class="form-label">Experience (years)</label>
                  <input
                    id="experience"
                    type="number"
                    class="form-control"
                    formControlName="experience"
                    min="0"
                    max="80"
                  />
                  @if (form.controls['experience'].touched && form.controls['experience'].invalid) {
                    <div class="text-danger small mt-1">Enter a valid number of years.</div>
                  }
                </div>

                <!-- License Number & Specialization are always required -->
                <div class="col-md-6">
                  <label for="licenseNumber" class="form-label">HPCSA Licence Number</label>
                  <input
                    id="licenseNumber"
                    type="text"
                    class="form-control"
                    formControlName="licenseNumber"
                  />
                  @if (
                    form.controls['licenseNumber'].touched && form.controls['licenseNumber'].invalid
                  ) {
                    <div class="text-danger small mt-1">License number is required.</div>
                  }
                </div>

                <div class="col-md-6">
                  <label for="specialization" class="form-label">Specialization</label>
                  <select
                    id="specialization"
                    class="form-select text-black"
                    formControlName="specialization"
                  >
                    <option value="" disabled selected>Select Specialization</option>
                    <option *ngFor="let option of specializationOptions" [value]="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  @if (
                    form.controls['specialization'].touched &&
                    form.controls['specialization'].invalid
                  ) {
                    <div class="text-danger small mt-1">Specialization is required.</div>
                  }
                </div>
              </div>

              <!-- ── Create Clinic Toggle ───────────────────────────── -->
              <div
                class="card border-primary border-opacity-25 bg-primary bg-opacity-10 rounded-3 p-3 mt-5 mb-2"
              >
                <div class="form-check form-switch d-flex align-items-start gap-3 m-0">
                  <input
                    class="form-check-input mt-1 flex-shrink-0"
                    style="width: 2.5rem; height: 1.25rem"
                    type="checkbox"
                    id="createClinicToggle"
                    role="switch"
                    [checked]="createClinic"
                    (change)="onCreateClinicChange($any($event.target).checked)"
                  />
                  <div>
                    <label class="form-check-label fw-semibold" for="createClinicToggle">
                      <i class="bi bi-hospital me-1 text-primary"></i>
                      Create my own practice
                    </label>
                    <div class="text-muted small mt-1">
                      Check this if you are opening or registering your own practice. Leave
                      unchecked if you will be joining an existing practice via invitation.
                    </div>
                  </div>
                </div>
              </div>

              <!-- ── Practice Details (only when createClinic = true) ── -->
              @if (createClinic) {
                <h4 class="mb-3 mt-5 text-primary fw-semibold">Practice Details</h4>
                <hr class="mt-0 mb-4" />

                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="practiceNumber" class="form-label"
                      >Practice Number <span class="text-danger">*</span></label
                    >
                    <input
                      id="practiceNumber"
                      type="text"
                      class="form-control"
                      formControlName="practiceNumber"
                    />
                    @if (
                      form.controls['practiceNumber'].touched &&
                      form.controls['practiceNumber'].invalid
                    ) {
                      <div class="text-danger small mt-1">Practice Number is required.</div>
                    }
                  </div>

                  <div class="col-md-6">
                    <label for="practiceName" class="form-label"
                      >Practice Name <span class="text-danger">*</span></label
                    >
                    <input
                      id="practiceName"
                      type="text"
                      class="form-control"
                      formControlName="practiceName"
                    />
                    @if (
                      form.controls['practiceName'].touched && form.controls['practiceName'].invalid
                    ) {
                      <div class="text-danger small mt-1">Practice Name is required.</div>
                    }
                  </div>
                </div>

                <!-- Educational Details -->
                <h4 class="mb-3 mt-5 text-primary fw-semibold">Educational Details</h4>
                <hr class="mt-0 mb-4" />

                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="highestQualification" class="form-label"
                      >Highest Qualification (e.g., MD, FRCS)
                      <span class="text-danger">*</span></label
                    >
                    <input
                      id="highestQualification"
                      type="text"
                      class="form-control"
                      formControlName="highestQualification"
                    />
                    @if (
                      form.controls['highestQualification'].touched &&
                      form.controls['highestQualification'].invalid
                    ) {
                      <div class="text-danger small mt-1">Highest Qualification is required.</div>
                    }
                  </div>

                  <div class="col-md-6">
                    <label for="learningInstitution" class="form-label"
                      >Institution Name <span class="text-danger">*</span></label
                    >
                    <input
                      id="learningInstitution"
                      type="text"
                      class="form-control"
                      formControlName="learningInstitution"
                    />
                    @if (
                      form.controls['learningInstitution'].touched &&
                      form.controls['learningInstitution'].invalid
                    ) {
                      <div class="text-danger small mt-1">Institution is required.</div>
                    }
                  </div>

                  <div class="col-md-6">
                    <label for="qualificationCertificateNumber" class="form-label"
                      >Qualification Certificate Number</label
                    >
                    <input
                      id="qualificationCertificateNumber"
                      type="text"
                      class="form-control"
                      formControlName="qualificationCertificateNumber"
                    />
                  </div>
                </div>

                <!-- Address Details -->
                <h4 class="mb-3 mt-5 text-primary fw-semibold">Practice Address</h4>
                <hr class="mt-0 mb-4" />

                <div class="row g-3">
                  <div class="col-12">
                    <label for="addressLine1" class="form-label"
                      >Address Line 1 <span class="text-danger">*</span></label
                    >
                    <input
                      id="addressLine1"
                      type="text"
                      class="form-control"
                      formControlName="addressLine1"
                    />
                    @if (
                      form.controls['addressLine1'].touched && form.controls['addressLine1'].invalid
                    ) {
                      <div class="text-danger small mt-1">Address Line 1 is required.</div>
                    }
                  </div>

                  <div class="col-12">
                    <label for="addressLine2" class="form-label"
                      >Address Line 2 <span class="text-muted small">(optional)</span></label
                    >
                    <input
                      id="addressLine2"
                      type="text"
                      class="form-control"
                      formControlName="addressLine2"
                    />
                  </div>

                  <div class="col-12">
                    <label for="addressLine3" class="form-label"
                      >Address Line 3 <span class="text-muted small">(optional)</span></label
                    >
                    <input
                      id="addressLine3"
                      type="text"
                      class="form-control"
                      formControlName="addressLine3"
                    />
                  </div>

                  <div class="col-md-6">
                    <label for="city" class="form-label"
                      >City <span class="text-danger">*</span></label
                    >
                    <input id="city" type="text" class="form-control" formControlName="city" />
                    @if (form.controls['city'].touched && form.controls['city'].invalid) {
                      <div class="text-danger small mt-1">City is required.</div>
                    }
                  </div>

                  <div class="col-md-6">
                    <label for="postalCode" class="form-label"
                      >Postal Code <span class="text-danger">*</span></label
                    >
                    <input
                      id="postalCode"
                      type="text"
                      class="form-control"
                      formControlName="postalCode"
                    />
                    @if (
                      form.controls['postalCode'].touched && form.controls['postalCode'].invalid
                    ) {
                      <div class="text-danger small mt-1">
                        Postal code is required (4–10 digits).
                      </div>
                    }
                  </div>
                </div>
              }
              <!-- end @if createClinic -->

              <!-- ── Submit ────────────────────────────────────────── -->
              <div class="d-flex justify-content-end mt-4">
                <button type="submit" class="btn btn-primary px-5" [disabled]="form.invalid">
                  <i class="bi bi-check2-circle me-2"></i>Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
}
